// Formatting helpers for the basketball performance cards (coach Team ›
// Performance and the athlete profile's Performance section). Pure
// functions, no React. Every helper returns EMPTY (the en dash) for a value
// the payload does not have, never a made-up number.
//
//   topPct(p)        "Top N%" (N = max(1, round(100 - p))), else EMPTY
//   topPctN(p)       that N as a number, else null
//   pctText(p)       "46th pct" (a descriptive metric's plain percentile), else EMPTY
//   ordinal(p)       "63rd", else EMPTY
//   signed1(v)       "+6.0" / "−2.0" / "0.0", else EMPTY
//   pct1(v)          "49.9%", else EMPTY
//   fixed(v, d)      v with d decimals ("103.6"), else EMPTY
//   shortDate(iso)   "2026-07-14" -> "Jul 14", else EMPTY
//   asOfDay(v, {year}) a saved copy's day in the viewer's time zone:
//                    "Oct 20" / "Oct 20, 2026", else ""
//   dateRange(a, b)  "May 15 – Jul 18"
//   bandOf(p)        "positive" (>= 60) / "attention" (35-59) / "critical" (< 35) / null
//   peerNote(n, noun) "vs 394 rotation players", else null
//   eventName(name)  "Session II (Memphis)" -> "Session II"
//   safeCopy(text)   the frontend safety net for payload text (B6)
//   pyRound(x)       Python's round(): half to even, as the payload rounds
//   APPROXIMATE_NOTE what the "≈" after a rank means
//   andList(items)   "A, B and C"
//   descriptiveLegend(names) what a descriptive metric's "46th pct" chip
//                    means: "Usage and 3PA rate: percentile of how much,
//                    not how well", else null
//
// Rounding follows the payload (Python's round, half to even), so a chip
// and the insight text that quotes the same percentile always agree: 86.5
// reads "86th" in both, never "87th" on the chip.

export const EMPTY = "–";

// What the "≈" after a rank means (RankChip's approximate mark), one
// sentence for both performance sections' ledes.
export const APPROXIMATE_NOTE = "≈ marks a rank from an approximate peer norm.";

// "A", "A and B", "A, B and C".
export const andList = (items) => {
  const list = (Array.isArray(items) ? items : []).filter(Boolean);
  return list.length < 2 ? list.join("") : `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
};

// What a descriptive metric's passive chip means (RankChip kind
// "descriptive", B5: pace, shot rates, usage, the roster figures; neither
// better nor worse, so the chip reads "46th pct", never "Top N%"). Both
// performance sections put this line in the legend of a card that shows
// such a chip: "Usage and 3PA rate: percentile of how much, not how well".
// Null without a name.
export const DESCRIPTIVE_MEANING = "percentile of how much, not how well";
export const descriptiveLegend = (names) => {
  const shown = (Array.isArray(names) ? names : []).filter(Boolean);
  return shown.length ? `${andList(shown)}: ${DESCRIPTIVE_MEANING}` : null;
};

// A finite number from a payload value, else null (null, undefined, "",
// booleans, NaN and Infinity are all "no value").
export const toNum = (v) => {
  if (v === null || v === undefined || v === "" || typeof v === "boolean") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// Python's round() to an integer: halves go to the even neighbour
// (0.5 -> 0, 1.5 -> 2, 86.5 -> 86, -2.5 -> -2).
export const pyRound = (x) => {
  const v = toNum(x);
  if (v === null) return null;
  const floor = Math.floor(v);
  const diff = v - floor;
  if (diff > 0.5) return floor + 1;
  if (diff < 0.5) return floor;
  return floor % 2 === 0 ? floor : floor + 1;
};

// The N of "Top N%": the share of the peer group at or above this
// percentile, never below 1.
export const topPctN = (p) => {
  const v = toNum(p);
  if (v === null) return null;
  return Math.max(1, pyRound(100 - v));
};

export const topPct = (p) => {
  const n = topPctN(p);
  return n === null ? EMPTY : `Top ${n}%`;
};

const suffixOf = (n) => {
  const abs = Math.abs(n);
  if (abs % 100 >= 11 && abs % 100 <= 13) return "th";
  return { 1: "st", 2: "nd", 3: "rd" }[abs % 10] || "th";
};

// 1st, 2nd, 3rd, 4th, 11th to 13th, 21st (the payload's _ord).
export const ordinal = (p) => {
  const n = pyRound(p);
  return n === null ? EMPTY : `${n}${suffixOf(n)}`;
};

// A descriptive metric's percentile (pace, a shot rate, usage, a roster
// figure: neither better nor worse) in plain words, "46th pct", never
// "Top N%", which reads as a quality rank; EMPTY without one.
export const pctText = (p) => (toNum(p) === null ? EMPTY : `${ordinal(p)} pct`);

// v with `decimals` places; EMPTY without a value.
export const fixed = (v, decimals = 1) => {
  const n = toNum(v);
  return n === null ? EMPTY : n.toFixed(decimals);
};

// "+6.0", "−2.0" (a true minus sign, as coachUtils.fmtSigned), "0.0".
export const signed1 = (v) => {
  const n = toNum(v);
  if (n === null) return EMPTY;
  const r = Number(n.toFixed(1));
  if (r === 0) return "0.0";
  return `${r > 0 ? "+" : "−"}${Math.abs(r).toFixed(1)}`;
};

// A 0-100 rate with one decimal: "49.9%".
export const pct1 = (v) => {
  const n = toNum(v);
  return n === null ? EMPTY : `${n.toFixed(1)}%`;
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

// "2026-07-14" (or a full ISO timestamp) -> "Jul 14". A string that is not
// an ISO date comes back as it is; nothing gives EMPTY.
export const shortDate = (iso) => {
  if (iso === null || iso === undefined || iso === "") return EMPTY;
  const m = String(iso).match(ISO_DATE);
  if (!m) return String(iso);
  const month = MONTHS[Number(m[2]) - 1];
  if (!month) return String(iso);
  return `${month} ${Number(m[3])}`;
};

// The day a saved copy dates from (an endpoint's dataStatus.asOf), in the
// viewer's own time zone, as the app's other "as of" notices read it:
// "Oct 20", or "Oct 20, 2026" with `year`. A bare "2026-10-20" is a
// calendar day already, so no time zone moves it. "" without a valid date.
export const asOfDay = (value, { year = false } = {}) => {
  if (value === null || value === undefined || typeof value === "boolean") return "";
  const raw = String(value).trim();
  if (!raw) return "";
  const bare = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (bare && !MONTHS[Number(bare[2]) - 1]) return "";
  const date = bare ? new Date(Number(bare[1]), Number(bare[2]) - 1, Number(bare[3])) : new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", year ? { month: "short", day: "numeric", year: "numeric" } : { month: "short", day: "numeric" });
};

// "May 15 – Jul 18" (en dash); one date alone when both are the same.
export const dateRange = (from, to) => {
  const a = shortDate(from);
  const b = shortDate(to);
  if (a === EMPTY) return b;
  if (b === EMPTY || a === b) return a;
  return `${a} – ${b}`;
};

// The coach snapshot's band dot (B5): positive at the 60th percentile and
// up, attention from the 35th, critical below. Null without a percentile.
export const bandOf = (p) => {
  const v = toNum(p);
  if (v === null) return null;
  if (v >= 60) return "positive";
  if (v >= 35) return "attention";
  return "critical";
};

// The peer group size beside small-group percentiles: "vs 394 rotation
// players", "vs 45 squads". Null when the size is unknown.
export const peerNote = (n, noun) => {
  const v = toNum(n);
  if (v === null || !noun) return null;
  return `vs ${v.toLocaleString("en-US")} ${noun}`;
};

// An event's short name: the parenthetical place is dropped
// ("Peach Jam (Augusta)" -> "Peach Jam").
export const eventName = (name) => (name ? String(name).replace(/\s*\(.*\)\s*$/, "").trim() : "");

// The frontend safety net for payload text (B6). The backend already
// rewrites every insight before it leaves the server, so on clean text this
// changes nothing; it only catches a string that slipped through:
//  - an em dash (or the horizontal bar the backend also treats as one)
//    becomes a comma;
//  - "C-RAM band" becomes "impact band" (B6's band heading), any other
//    "C-RAM" becomes "Impact rating";
//  - the data provider's name never reaches the screen: the known
//    linked-records caveat is reworded, any other mention is dropped
//    with its possessive (a closing "from X's." leaves "from.").
const LINK_CAVEAT = /Lower bound: split cerebro ids don['’]t link\./gi;
const VENDOR_POSSESSIVE = /\b(?:cerebro|sports\s?radar|sportradar|genius)(?:\s+sports)?['’]s\s+/gi;
const VENDOR_WORD = /\s*\b(?:cerebro|sports\s?radar|sportradar|genius)(?:\s+sports)?(?:['’]s)?\b/gi;

export const safeCopy = (text) => {
  if (text === null || text === undefined) return "";
  return String(text)
    .replace(LINK_CAVEAT, "Lower bound: some players' later records are not linked.")
    .replace(/\s*[\u2014\u2015]\s*/g, ", ")
    .replace(/\bC-RAM band\b/g, "impact band")
    .replace(/\bC-RAM\b/g, "Impact rating")
    .replace(VENDOR_POSSESSIVE, "")
    .replace(VENDOR_WORD, "")
    .replace(/ {2,}/g, " ")
    .trim();
};
